import Card from "@pathwright/ui/src/components/card/Card"
import CardBlock from "@pathwright/ui/src/components/card/CardBlock"
import CardLink from "@pathwright/ui/src/components/card/CardLink"
import { Card as CardT } from "@pathwright/ui/src/components/card/types"
import FullscreenConfirm from "@pathwright/ui/src/components/confirm/FullscreenConfirm"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import { useMutationWithDefaults } from "@pathwright/utils/src/graphql/mutation"
import PropTypes from "prop-types"
import {
  FeatureAction,
  FeatureContextInput,
  FeatureKey,
  FeaturePermission,
  useCommunityEnableMutation
} from "../../api/generated"
import useTagPermissions from "../../tag/useTagPermissions"
import FeatureBlockList from "../block/FeatureBlockList"
import { useFeaturePermission } from "../form/FeaturePermissionsForm"
import withFeatureContext from "./withFeatureContext"

const ConfirmEnable = ({ ...confirmProps }) => {
  const { t } = useTranslate()

  return (
    <FullscreenConfirm
      icon="caution-triangle"
      heading={t("Enable Community in this Cohort?")}
      body={t(
        "Enable members to post discussions in the Path and access features in the Community Tab."
      )}
      confirmPrompt={t("Enable Community")}
      cancelPrompt={t("Nevermind, go back")}
      stack
      {...confirmProps}
    />
  )
}

const ConfirmDisable = ({ ...confirmProps }) => {
  const { t } = useTranslate()

  return (
    <FullscreenConfirm
      icon="caution-triangle"
      heading={t("Disable Community in this Cohort?")}
      body={t(
        "Prevent members from posting discussions in the Path or accessing features in the Community Tab."
      )}
      confirmPrompt={t("Yes, Disable Community  ")}
      cancelPrompt={t("Nevermind, go back")}
      stack
      {...confirmProps}
    />
  )
}

const ManageCohortFeatures = withFeatureContext(
  ({
    card,
    context,
    communityEnabled,
    onChangeCommunityEnabled
  }: {
    card: CardT
    context: FeatureContextInput & { cohort_id: number }
    communityEnabled: boolean
    onChangeCommunityEnabled?: (enabled: boolean) => void
  }) => {
    const { t } = useTranslate()
    const tagPermissions = useTagPermissions({ context })
    const permissions = {
      [FeatureKey.CohortActivityFeed]: useFeaturePermission({
        featureKey: FeatureKey.CohortActivityFeed,
        featureAction: FeatureAction.View,
        context
      }),
      [FeatureKey.CohortMemberDirectory]: useFeaturePermission({
        featureKey: FeatureKey.CohortMemberDirectory,
        featureAction: FeatureAction.View,
        context
      })
    }

    const [communityEnableMutation] = useMutationWithDefaults(
      useCommunityEnableMutation,
      {
        variables: {
          cohortId: context.cohort_id
        },
        onCompleted: (result) => {
          onChangeCommunityEnabled?.(
            !!result.updateGroupDiscussionEnabled?.discussion_enabled
          )
        }
      }
    )

    const handleEnableCommunity = (enable: boolean) => {
      const mutationPromises = Object.values(permissions)
        .filter((config) => config.canUpdate)
        .map((config) =>
          config.mutation[0]({
            variables: {
              permissionInContext: enable
                ? FeaturePermission.Everyone
                : FeaturePermission.Disabled
            }
          })
        )

      const communityEnableMutationPromise = communityEnableMutation({
        variables: {
          communityEnabled: enable
        }
      })

      return Promise.all([...mutationPromises, communityEnableMutationPromise])
    }

    // only allow user to enable/disable community if they have `view` update permissions
    // for all features
    const canEnableCommunity = Object.values(permissions).reduce(
      (bool, { canUpdate }) => bool && !!canUpdate,
      true
    )

    // Only show those features which the user can edit.
    const featureKeys = Object.entries(permissions).reduce(
      (keys, [key, { canUpdate }]) => {
        if (canUpdate) keys.push(key as FeatureKey)
        return keys
      },
      [] as FeatureKey[]
    )

    return (
      <Card card={card} title={t("Community Features")} emphasis="secondary">
        <CardBlock paddingTop={false}>
          <FeatureBlockList
            featureAction={FeatureAction.View}
            featureKeys={featureKeys}
            context={context}
            showTagFeatureBlock={tagPermissions.canEdit}
          />
          {canEnableCommunity ? (
            communityEnabled ? (
              <ConfirmDisable onConfirm={() => handleEnableCommunity(false)}>
                {({ confirm }: { confirm: () => void }) => (
                  <CardLink
                    label={t("Disable Community features")}
                    meta={t(
                      "Disable discussion and all other Community Features"
                    )}
                    onClick={confirm}
                    // hack to show chevron-right icon
                    to={window.location.pathname}
                  />
                )}
              </ConfirmDisable>
            ) : (
              <ConfirmEnable onConfirm={() => handleEnableCommunity(true)}>
                {({ confirm }: { confirm: () => void }) => (
                  <CardLink
                    label={t("Enable Community features")}
                    meta={t(
                      "Enable discussion and all other Community Features"
                    )}
                    onClick={confirm}
                    // hack to show chevron-right icon
                    to={window.location.pathname}
                  />
                )}
              </ConfirmEnable>
            )
          ) : null}
        </CardBlock>
      </Card>
    )
  }
)

ManageCohortFeatures.displayName = "ManageCohortFeatures"

ManageCohortFeatures.propTypes = {
  ...PropTypes.shape({ ...withFeatureContext._propTypes }),
  communityEnabled: PropTypes.bool.isRequired,
  onChangeCommunityEnabled: PropTypes.func
}

export default ManageCohortFeatures
